import React, { useState, useEffect } from 'react';
import { coachingAnalytics } from '../../api';
import './TeamHours.css';

const REFRESH_MS = 5 * 60 * 1000;

// "YYYY-MM-DD" -> a Date whose UTC parts are that calendar day, so the viewer's timezone never shifts it
const dateOf = (key) => { const [y, m, d] = key.split('-').map(Number); return new Date(Date.UTC(y, m - 1, d)); };
const fmt = (key, opts) => dateOf(key).toLocaleDateString([], { ...opts, timeZone: 'UTC' });
const range = (from, to) => {
  const a = dateOf(from);
  const b = dateOf(to);
  const sameMonth = a.getUTCMonth() === b.getUTCMonth() && a.getUTCFullYear() === b.getUTCFullYear();
  return sameMonth
    ? `${a.getUTCDate()} - ${fmt(to, { day: 'numeric', month: 'short' })}`
    : `${fmt(from, { day: 'numeric', month: 'short' })} - ${fmt(to, { day: 'numeric', month: 'short', year: b.getUTCFullYear() !== a.getUTCFullYear() ? '2-digit' : undefined })}`;
};
const h = (n) => `${Math.round(n * 10) / 10}`;

// Venue bars: how the hours split across venues, and who taught how much at each
const VenueRows = ({ venues }) => {
  if (!venues || venues.length === 0) return <div className="th-empty">No lessons in this period.</div>;
  const max = Math.max(...venues.map(v => v.hours), 0.0001);
  return venues.map(v => (
    <div key={v.name} style={{ marginBottom: 6 }}>
      <div className="th-row">
        <span className="th-row-name" title={v.name}>{v.name}</span>
        <span className="th-bar"><i style={{ width: `${Math.max(2, (v.hours / max) * 100)}%` }} /></span>
        <span className="th-row-val">{h(v.hours)}h · {v.share}%</span>
      </div>
      <div className="th-venue-detail" style={{ margin: '0 0 2px 0' }}>
        {v.lessons} {v.lessons === 1 ? 'lesson' : 'lessons'}{v.byCoach.length > 0 ? ` - ${v.byCoach.map(c => `${c.name} ${h(c.hours)}h`).join(', ')}` : ''}
      </div>
    </div>
  ));
};

// Head coach: pick a period (this week, this month, past 3 months, ...) and see its hours, venues and coaches; every week below.
const TeamSummary = ({ tick = 0 }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);
  const [selected, setSelected] = useState('currentWeek');
  const [openWeek, setOpenWeek] = useState('');

  useEffect(() => {
    let cancelled = false;
    const load = (fresh) => coachingAnalytics.getSummary(fresh)
      .then(res => { if (!cancelled) { setData(res.data); setError(false); } })
      .catch(() => { if (!cancelled) setError(true); });
    load(tick > 0);
    const timer = setInterval(() => { if (!document.hidden) load(false); }, REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [tick]);

  if (error && !data) return <div className="th-card"><p className="th-error">Could not load the hour totals.</p></div>;
  if (!data) return <div className="th-card"><div className="th-empty">Loading hour totals...</div></div>;

  const { periods, weeks, coaches } = data;
  const period = periods.find(p => p.key === selected) || periods[0];
  const maxWeek = Math.max(...weeks.map(w => w.hours), 0.0001);
  const maxCoach = Math.max(...period.perCoach.map(c => c.hours), 0.0001);

  return (
    <>
      <div className="th-card">
        <h3 className="th-h">Hours by period</h3>
        <p className="th-sub">Pick a period. The numbers, the venues and the coaches below follow it.</p>
        <div className="th-pchips" role="tablist" aria-label="Period">
          {periods.map(p => (
            <button
              key={p.key}
              type="button"
              role="tab"
              aria-selected={selected === p.key}
              className="th-pchip"
              onClick={() => setSelected(p.key)}
            >
              <span>{p.label}</span>
              <b>{h(p.hours)}h</b>
            </button>
          ))}
        </div>

        <div className="th-period-head">
          <h4 className="th-h" style={{ margin: 0 }}>{period.label}</h4>
          <span className="th-sub" style={{ margin: 0 }}>{range(period.from, period.to)}</span>
        </div>

        <div className="th-tiles">
          <div className="th-tile"><b>{h(period.hours)}</b><span>Coaching hours</span></div>
          <div className="th-tile"><b>{period.lessons}</b><span>Lessons</span></div>
          <div className="th-tile"><b>{h(period.hoursDone)}</b><span>Hours done</span></div>
          <div className="th-tile"><b>{h(period.hoursUpcoming)}</b><span>Hours to come</span></div>
        </div>
      </div>

      <div className="th-card">
        <h3 className="th-h">By venue - {period.label.toLowerCase()}</h3>
        <VenueRows venues={period.venues} />
      </div>

      <div className="th-card">
        <h3 className="th-h">By coach - {period.label.toLowerCase()}</h3>
        {period.perCoach.map(c => (
          <div key={c._id} className="th-row">
            <span className="th-row-name" title={c.name}>{c.name}</span>
            <span className="th-bar"><i style={{ width: `${c.hours === 0 ? 0 : Math.max(2, (c.hours / maxCoach) * 100)}%` }} /></span>
            <span className="th-row-val">{h(c.hours)}h · {c.lessons}</span>
          </div>
        ))}
      </div>

      <div className="th-card">
        <h3 className="th-h">All weeks</h3>
        <p className="th-sub">Monday to Sunday. Tap a week to see its venues. The current week is highlighted; weeks ahead show what is already booked.</p>
        <div className="th-scroll">
          <table className="th-table th-weeks">
            <thead>
              <tr>
                <th>Week</th>
                <th>Hours</th>
                <th>Lessons</th>
                {coaches.map(c => <th key={c._id}>{c.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {weeks.map(w => (
                <React.Fragment key={w.start}>
                  <tr
                    className={`th-weekrow${w.isCurrent ? ' th-current' : ''}${w.isFuture ? ' th-ahead' : ''}`}
                    onClick={() => setOpenWeek(openWeek === w.start ? '' : w.start)}
                    aria-expanded={openWeek === w.start}
                  >
                    <td>
                      {openWeek === w.start ? '▾ ' : '▸ '}{range(w.start, w.end)}
                      {w.isCurrent && <span className="th-tag">now</span>}
                    </td>
                    <td>
                      <span className="th-wbar"><i style={{ width: `${w.hours === 0 ? 0 : Math.max(3, (w.hours / maxWeek) * 100)}%` }} /></span>
                      <strong>{h(w.hours)}</strong>
                    </td>
                    <td>{w.lessons}</td>
                    {w.perCoach.map(c => <td key={c._id}>{c.hours ? h(c.hours) : '-'}</td>)}
                  </tr>
                  {openWeek === w.start && (
                    <tr className="th-weekdetail">
                      <td colSpan={3 + coaches.length}>
                        <VenueRows venues={w.venues} />
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </>
  );
};

export default TeamSummary;
